import { useContext, useState, useEffect } from 'react';
import {
  ClosetContext,
  ClosetBrokeredValue
} from '@/components/context/ClosetContext/ClosetContext';
import { CLOSET_SLOT_TIER_SLUG, ClosetValidationStatus } from '@/types/Closet';
import { useLoggedInUser } from './user';
import { getClosetStatusForProductTier } from '@/util/closet_client';
import { Cart } from '@/types/Cart';

/**
 * Returns Closet value along with actions to interact with the user's closet
 */
export const useCloset = (): ClosetBrokeredValue => {
  const context = useContext(ClosetContext);

  return context;
};

type GetClosetValidationHookValue = {
  loading: boolean;
  closetValidationStatus?: null | ClosetValidationStatus;
  closetValidationSwapDate?: string;

  /** Resets the hook to re-run validation calls */
  revalidate: () => void;
};

/**
 * Used to get the closet validation status for a particular product ID.
 * @client Client side only hook
 * @param productId
 */
export const useClosetValidationForProductTier = (
  productId: string,
  productTier: CLOSET_SLOT_TIER_SLUG,
  cart: Cart
): GetClosetValidationHookValue => {
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [closetValidationStatus, setClosetValidationStatus] =
    useState<GetClosetValidationHookValue['closetValidationStatus']>(null);
  const [closetValidationSwapDate, setClosetValidationSwapDate] =
    useState<string>();

  const user = useLoggedInUser();

  const revalidate = () => {
    setInitialized(false);
  };

  useEffect(() => {
    const init = async () => {
      setInitialized(true);
      setLoading(true);

      // Do nothing if the user is not logged in
      if (!user) {
        setLoading(false);
        setClosetValidationStatus(null);
        return;
      }

      // Look at the cart contents first prior to closet validation
      const matchingCartItemProduct = cart.items.find(
        (cartItem) => cartItem.product.id === productId
      );
      if (matchingCartItemProduct) {
        setClosetValidationStatus(ClosetValidationStatus.ItemInCart);
        setLoading(false);
        return;
      }

      try {
        const validationResponse = await getClosetStatusForProductTier({
          closetTierSlug: productTier
        });

        if (validationResponse?.is_valid) {
          setClosetValidationStatus(undefined);
          setClosetValidationSwapDate(undefined);
        } else {
          // If cart is invalid, but could be valid if emptied, set EmptyCart status
          if (validationResponse?.is_valid_with_cart_emptied) {
            setClosetValidationStatus(ClosetValidationStatus.EmptyCart);
            setClosetValidationSwapDate(undefined);
          } else {
            let errorCode = validationResponse?.error?.error_code as
              | ClosetValidationStatus
              | null
              | undefined;

            // Privee users should never be told to upgrade — remap to TooEarly
            if (
              productTier === CLOSET_SLOT_TIER_SLUG.PRIVEE &&
              (errorCode === ClosetValidationStatus.TooEarlyOrUpgrade ||
                errorCode === ClosetValidationStatus.UpgradeOrReturn ||
                errorCode === ClosetValidationStatus.Upgrade)
            ) {
              errorCode = ClosetValidationStatus.TooEarly;
            }

            setClosetValidationStatus(errorCode);
            setClosetValidationSwapDate(
              validationResponse?.error?.error_swap_date
            );
          }
        }
      } catch (e) {
        console.error(e);
        setLoading(false);
        setClosetValidationStatus(null);
        return;
      }

      setLoading(false);
    };

    if (!initialized) {
      init();
    }
  }, [initialized, user, setClosetValidationStatus, productTier]);

  return {
    loading,
    closetValidationStatus,
    closetValidationSwapDate,
    revalidate
  };
};
